import { useState } from "react";
import { Upload, Download, Images, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, download, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/opl/Common";
import { GoldBtn, GhostBtn } from "@/components/opl/Crud";

const TEMPLATE = "name,role,category,club,dob,phone,email,batting_style,bowling_style,wicketkeeper,experience,base_price,tags,notes\nExample Player,Batsman,Senior,Adajan,,,,Right-hand bat,,no,,,,\n";

export default function ImportExport() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [imgRes, setImgRes] = useState(null);
  const run = async (dry) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post(`/players/import?dry_run=${dry}`, fd);
      setPreview(r.data);
      if (!dry) toast.success(`${r.data.created} players imported`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const images = async (files) => {
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    setBusy(true);
    try { const r = await api.post("/players/bulk-images", fd); setImgRes(r.data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const tpl = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv" }));
    a.download = "opl_player_import_template.csv";
    a.click();
  };
  return (
    <div>
      <PageHeader title="Import & Export" crumbs={["Players", "Import & Export"]} />
      <div className="grid lg:grid-cols-2 gap-5">
        <section className="surface rounded-2xl p-6" data-testid="import-section">
          <p className="eyebrow">Bulk import (CSV / Excel)</p>
          <p className="text-sm text-slate-400 mt-2">Required columns: <b>name, role, category</b>. Rows are validated and duplicates are flagged before anything is saved.</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <GhostBtn testid="download-template-btn" onClick={tpl}><Download className="h-4 w-4" />Template</GhostBtn>
            <label className="cursor-pointer flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm">
              <Upload className="h-4 w-4" />{file ? file.name : "Choose file"}
              <input type="file" accept=".csv,.xlsx" className="hidden" data-testid="import-file-input" onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} />
            </label>
            <GhostBtn testid="import-validate-btn" disabled={!file || busy} onClick={() => run(true)}>Validate</GhostBtn>
            <GoldBtn testid="import-commit-btn" disabled={!preview?.valid || busy} onClick={() => run(false)}>{busy && <Loader2 className="h-4 w-4 animate-spin" />}Import {preview?.valid || 0} valid rows</GoldBtn>
          </div>
          {preview && (
            <div className="mt-4 max-h-80 overflow-y-auto text-sm" data-testid="import-preview">
              <p className="mb-2">{preview.total} rows · <span className="text-emerald-300">{preview.valid} valid</span> · <span className="text-red-300">{preview.total - preview.valid} with errors</span>{!preview.dry_run && ` · ${preview.created} created`}</p>
              {preview.rows.map((r) => <div key={r.row} className="flex justify-between border-b border-white/5 py-1"><span>Row {r.row}: {r.name || "—"}</span><span className={r.errors.length ? "text-red-300" : "text-emerald-300"}>{r.errors.join(", ") || "OK"}</span></div>)}
            </div>
          )}
        </section>
        <section className="surface rounded-2xl p-6" data-testid="bulk-images-section">
          <p className="eyebrow">Bulk image upload</p>
          <p className="text-sm text-slate-400 mt-2">Name each image after the player code, e.g. <span className="num">OPL-P001.jpg</span>. JPG, PNG or WEBP up to 5MB.</p>
          <label className="mt-4 cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full gold-bg text-sm font-bold">
            <Images className="h-4 w-4" />Select images
            <input type="file" multiple accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="bulk-images-input" onChange={(e) => images(e.target.files)} />
          </label>
          {imgRes && <div className="mt-4 text-sm space-y-1">{imgRes.map((r) => <div key={r.file} className={r.ok ? "text-emerald-300" : "text-red-300"}>{r.file}: {r.ok ? `→ ${r.player}` : r.error}</div>)}</div>}
          <p className="eyebrow mt-10">Export player database</p>
          <div className="flex gap-2 mt-3">
            {["csv", "xlsx", "pdf"].map((f) => <GhostBtn key={f} testid={`export-players-${f}`} onClick={() => download(`/players-export?fmt=${f}`, `opl_players.${f}`).catch((e) => toast.error(errMsg(e)))}><Download className="h-4 w-4" />{f.toUpperCase()}</GhostBtn>)}
          </div>
        </section>
      </div>
    </div>
  );
}
